import * as React from 'react';
import { forwardRef, Ref, useEffect, useImperativeHandle, useMemo, useState } from 'react';

import eventService from '@service/eventService';

import { TablePagination, TablePaginationProps } from '../components/TablePagination';
import { TablePaginationRef } from '../TableContext';
import { TableServices } from '../TableService';

function DynamoTablePagination(props: TablePaginationProps, ref: Ref<TablePaginationRef>) {
  const { lastEvaluatedKey, onLastEvaluatedKeyChange, paginationData } = props;

  const [paginationStates, setPaginationStates] = useState<(string | undefined)[]>(
    paginationData?.paginationStates ?? [undefined],
  );
  const [currentPageIndex, setCurrentPageIndex] = useState(paginationData?.currentPageIndex ?? 0);
  const [lastPageHit, setLastPageHit] = useState(false);

  const hasNextPage = useMemo(
    () => paginationStates.length - 1 > currentPageIndex + 1,
    [currentPageIndex, paginationStates],
  );
  const hasPreviousPage = useMemo(() => currentPageIndex > 0, [currentPageIndex, paginationStates]);

  const handleSetPageIndex = (newIndex: number, newKey?: string) => {
    setCurrentPageIndex(newIndex);
    onLastEvaluatedKeyChange?.(newKey || paginationStates?.[newIndex] || undefined);
  };

  const handleNextPage = (lastEvaluatedKey?: string) => {
    if (lastEvaluatedKey) {
      const keyIndex = paginationStates.findIndex((value) => value === lastEvaluatedKey);

      if (keyIndex >= 0) {
        handleSetPageIndex(currentPageIndex + 1, lastEvaluatedKey);
      } else {
        const newPaginationState = [...paginationStates];
        newPaginationState.push(lastEvaluatedKey);
        setPaginationStates(newPaginationState);
        handleSetPageIndex(newPaginationState.length - 1, lastEvaluatedKey);
      }
    }
  };

  const handlePrevPage = () => {
    let newIndex = currentPageIndex - 1;
    if (newIndex < 0) newIndex = 0;
    handleSetPageIndex(newIndex);
  };

  const handleResetPagination = () => {
    setPaginationStates(['']);
    setCurrentPageIndex(0);
    setLastPageHit(false);
  };

  const handleLastPageHit = () => {
    setLastPageHit(true);
  };

  useEffect(() => {
    if ((lastEvaluatedKey === '' && !lastPageHit) || (!hasNextPage && !lastEvaluatedKey)) {
      handleLastPageHit();
    }
  }, [hasNextPage, lastEvaluatedKey, lastPageHit]);

  useEffect(() => {
    eventService.on(TableServices.ResetTablePagination, handleResetPagination);
    return () => {
      eventService.removeListener(TableServices.ResetTablePagination);
    };
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      paginationStates,
      currentPageIndex,
    }),
    [currentPageIndex, paginationStates],
  );

  return (
    <TablePagination
      {...props}
      speculativeNumbering
      lastPageHit={lastPageHit}
      getCanNextPage={() => hasNextPage || !!lastEvaluatedKey}
      getCanPreviousPage={() => hasPreviousPage}
      nextPage={() => handleNextPage(lastEvaluatedKey)}
      previousPage={handlePrevPage}
      setPageIndex={handleSetPageIndex}
      getPageCount={() => paginationStates.length}
      getPageIndex={() => currentPageIndex}
    />
  );
}

const reffed = forwardRef(DynamoTablePagination);

export { reffed as DynamoTablePagination };
